// Utilities for deriving *received* income from raw bank transactions.
//
// The Dashboard's "Income › Received" figure is money that actually landed in
// the account this month. Plaid's sign convention is that a **negative** amount
// is a credit/deposit (money in) and a positive amount is a debit (money out).
// Internal account-to-account movements and online transfers are NOT income, so
// they are excluded — this mirrors the Transfer-category rules used on the
// Transactions page so the two surfaces agree.

export interface IncomeTransactionLike {
  amount: number;
  date?: string;
  name?: string;
  merchant_name?: string;
  internal_category?: string[];
}

// Name/merchant patterns that indicate a transfer between accounts rather than
// real income (kept in sync with TransactionsPage.isOnlineTransfer /
// isInternalTransfer).
const TRANSFER_NAME_PATTERNS: RegExp[] = [
  /online transfer/,
  /real ?time transfer/,
  /real-time transfer/,
  /transfer recd from/,
  /transfer rcvd from/,
  /transfer received from/,
  /transfer from/,
  /transfer to/,
  /internal transfer/,
  /account transfer/,
  /balance transfer/,
  /^transfer\s/,
  /deposit.*transfer/,
  /withdrawal.*transfer/,
  /chk.*\d{4}/, // e.g. "CHK ...9032"
  /sav.*\d{4}/, // e.g. "SAV ...3688"
  /checking.*transfer/,
  /savings.*transfer/,
];

// Plaid internal_category codes that represent internal bank operations, never
// external income.
const TRANSFER_CATEGORY_CODES = [
  "ACCT_XFER",
  "BILLPAY",
  "ACH_CREDIT",
  "DEBIT_CARD",
  "DEPOIST",
  "LOAN__PMT",
  "MISC_DEBIT",
  "PARTNERFI_TO_CHASE",
];

export const isTransferTransaction = (txn: IncomeTransactionLike): boolean => {
  const categories = Array.isArray(txn.internal_category)
    ? txn.internal_category
    : [];
  if (categories.some((cat) => TRANSFER_CATEGORY_CODES.includes(cat))) {
    return true;
  }
  const name = (txn.name || "").toLowerCase();
  const merchant = (txn.merchant_name || "").toLowerCase();
  return TRANSFER_NAME_PATTERNS.some(
    (pattern) => pattern.test(name) || pattern.test(merchant)
  );
};

// A transaction is income when it is a credit (negative amount in Plaid) and is
// not a transfer.
export const isIncomeTransaction = (txn: IncomeTransactionLike): boolean => {
  const amount = Number(txn.amount);
  if (!Number.isFinite(amount) || amount >= 0) return false;
  return !isTransferTransaction(txn);
};

// Sum the received income (money in) for transactions that fall within the
// current calendar month, excluding transfers. Returns a positive number.
export const getCurrentMonthReceivedIncomeTotal = (
  transactions: IncomeTransactionLike[]
): number => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  return transactions.reduce((total, txn) => {
    if (!isIncomeTransaction(txn)) return total;
    if (txn.date) {
      const txDate = new Date(txn.date);
      if (
        Number.isNaN(txDate.getTime()) ||
        txDate.getFullYear() !== year ||
        txDate.getMonth() !== month
      ) {
        return total;
      }
    }
    return total + Math.abs(Number(txn.amount));
  }, 0);
};
